<template>
	<div class="component-carousel-wrapper">
		<div class="component-carousel-viewport">
			<div class="component-container component-carousel">
				<slot></slot>
			</div>
		</div>
		<div class="carousel-controls">
			<button class="prev-button" @click="onPrevClick">
				<fa :icon="['fas', 'arrow-left']" />
			</button>
			<button class="next-button" @click="onNextClick">
				<fa :icon="['fas', 'arrow-right']" />
			</button>
		</div>
		<div v-if="total_slides > 1" class="carousel-dots">
			<button
				v-for="n in total_slides"
				:key="n"
				class="dot"
				:class="{ 'dot-active': n === current_slide + 1 }"
				@click="$emit('update:currentSlide', n - 1)"
			></button>
		</div>
	</div>
</template>

<script lang="ts">
	import { Options, Vue } from 'vue-class-component';
	@Options({
		name: 'Carousel',
		props: {
			total_slides: {
				type: Number,
				required: true,
			},
			current_slide: {
				type: Number,
				default: 1,
			},
		},
		emits: ['update:currentSlide', 'prev', 'next'],
	})
	export default class Carousel extends Vue {
		public total_slides!: number;
		public current_slide!: number;
		public auto_slide_interval: number | null = null;
		public auto_slide_delay: number = 10000; // Default auto-slide delay in milliseconds

		public get isFirstSlide(): boolean {
			return this.current_slide === 1;
		}

		public get isLastSlide(): boolean {
			return this.current_slide === this.total_slides;
		}

		public onPrevClick(): void {
			this.$emit('prev');
			this.resetAutoSlide();
		}

		public onNextClick(): void {
			this.$emit('next');
			this.resetAutoSlide();
		}

		public startAutoSlide(): void {
			this.clearAutoSlide();
			this.auto_slide_interval = window.setInterval(() => {
				const nextSlide = (this.current_slide + 1) % this.total_slides;
				this.$emit('update:currentSlide', nextSlide);
			}, this.auto_slide_delay);
		}

		public clearAutoSlide(): void {
			if (this.auto_slide_interval !== null) {
				clearInterval(this.auto_slide_interval);
				this.auto_slide_interval = null;
			}
		}

		public resetAutoSlide(): void {
			this.startAutoSlide();
		}

		public beforeUnmount(): void {
			if (this.auto_slide_interval !== null) {
				clearInterval(this.auto_slide_interval);
			}
		}

		public mounted(): void {
			if (this.current_slide < 0 || this.current_slide > this.total_slides) {
				this.$emit('update:currentSlide', 0);
			}
			this.startAutoSlide();
		}
	}
</script>

<style lang="scss" scoped>
	@import '../assets/styles/base';

	.component-carousel-wrapper {
		position: relative;
		width: 100%;
		height: 100%;
		.component-carousel-viewport {
			width: 100%;
			overflow: hidden;
			aspect-ratio: 4/3;
			@media screen and (max-width: 760px) {
				width: 300px;
			}
		}
		.prev-button {
			position: absolute;
			left: -16px;
			top: 50%;
			@media (max-width: 1200px) {
				top: 33%;
			}
			transform: translateY(-50%);
			width: 32px;
			height: 32px;
			background: rgba(white, 0.9);
			border: none;
			border-radius: 50%;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
			font-size: 1.2rem;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: #333;
			opacity: 0;
			transition: opacity 0.3s ease;
			&:hover {
				background-color: white;
				box-shadow: rgba(white, 0.8) 0px 3px 8px;
				transition: box-shadow 0.2s ease;
			}
		}
		.next-button {
			position: absolute;
			right: -16px;
			top: 50%;
			@media (max-width: 1200px) {
				top: 33%;
			}
			transform: translateY(-50%);
			width: 32px;
			height: 32px;
			background: rgba(white, 0.9);
			border: none;
			border-radius: 50%;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
			font-size: 1.2rem;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: #333;
			opacity: 0;
			transition: opacity 0.3s ease;
			&:hover {
				background-color: white;
				box-shadow: rgba(white, 0.8) 0px 3px 8px;
				transition: box-shadow 0.2s ease;
			}
		}
		&:hover {
			.prev-button,
			.next-button {
				opacity: 1;
			}
		}
	}

	.component-carousel {
		display: flex;
		align-items: center;
		border-radius: 0.5rem;
		overflow: hidden;
		position: relative;
		height: 100%;
		width: 100%;
	}

	.carousel-dots {
		position: absolute;
		top: -25px;
		display: flex;
		gap: 0.5rem;
		line-height: 0;

		.dot {
			width: 10px !important;
			height: 10px !important;
			border-radius: 50% !important;
			border: 0;
			padding: 0 !important;
			margin: 0 !important;
			box-sizing: content-box !important;
			font-size: 0 !important;
			line-height: 0 !important;
			background-color: #7084f4;
			display: inline-block;
			transition: background-color 0.3s ease;
			cursor: pointer;
			// box-shadow: 0 0 0 1px #ccc;
			vertical-align: middle;

			&.dot-active {
				background-color: white;
				@media (max-width: 1200px) {
					background-color: #b9b9b9;
				}
			}
		}
	}
</style>
